import React, { useRef, useCallback, useState } from "react";
import { Formik } from "formik";
import Divider from "~/components/divider";
import {
  Container,
  Header,
  Toolbar,
  FormContainer,
  HeaderData,
  Row,
  ReplaceContainer,
  ImageButton,
  SearchContainer,
  CheckArea,
  FormData,
  InputItem,
} from "~/views/match-day/styles";
import {
  InputLabel,
  Autocomplete,
  InputGroup,
  InputMask,
  Select,
} from "~/components/form";
import logo from "~/assets/logo.png";
import {
  MdAdd,
  MdClear,
  MdCheckCircle,
  MdSave,
  MdFilterList,
  MdViewModule,
} from "react-icons/md";
import useMatchDay from "~/view-data/use-match-day";
import useAuth from "~/view-data/use-auth";
import _ from "lodash";
import { formats } from "~/helper";
import { MASK_CPF } from "~/constants";
import userImage from "~/assets/user.svg";
import MatchDaySchema from "~/views/match-day/schema";
import useEventListener from "~/hooks/use-event-listener";
import { ProtocolCOVIDModal } from "~/views/match-day/covid";

const STATUS = {
  approved: "Aprovado",
  reproved: "Reprovado",
  pending: "Aguardando aprovação",
};

function MatchDay({ acls }) {
  const [searchCredential, setSearchCredential] = useState("");
  const refEmployeeSearch = useRef(null);
  const refEmployeeInput = useRef(null);
  const refCredentialNumber = useRef(null);
  const refBtnCheckInOut = useRef(null);
  const canWrite = acls.includes("W");
  const { state, actions } = useMatchDay();
  const { state: authState } = useAuth();
  const user = _.get(authState, 'user') || {};

  const nextFocus = (reference) => {
    if (reference && reference.current) {
      setTimeout(() => {
        reference.current.focus();
      }, 300);
    }
  };

  const isDisabledButtonCheck = useCallback((values) => {
    const checkInDate = _.get(values, "employee.checkInDate");
    const checkOutDate = _.get(values, "employee.checkOutDate");
    return checkInDate && checkOutDate;
  }, []);

  const getButtonLabelCheck = useCallback((values) => {
    const checkInDate = _.get(values, "employee.checkInDate");
    return !checkInDate ? "Check-in [F6]" : "Check-out";
  }, []);

  const handleChangeSearchCredentialNumber = (event) => {
    setSearchCredential(event.target.value);
  };

  const handleSaveEmployee = (kind, values) => {
    actions.saveEmployee(kind, values);
    nextFocus(refEmployeeSearch);
  };

  useEventListener({
    name: "keydown",
    handler: onKeyDown,
    elementId: "white-matchday-credential-search",
  });

  useEventListener({
    name: "keydown",
    handler: onKeyDownPulseira,
    elementId: "white-matchday-pulseira",
  });

  async function onKeyDown(event) {
    const charCode = event.which || event.keyCode;

    if (charCode === 27) {
      setSearchCredential("");
    }

    if (charCode === 13) {
      const result = await actions.searchByCredentialNumber(searchCredential);
      if (!result) {
        event.target.focus();
      } else {
        setSearchCredential("");
        nextFocus(refCredentialNumber);
      }
      event.preventDefault();
    }
  }

  async function onKeyDownPulseira(event) {
    const charCode = event.which || event.keyCode;

    if (
      charCode === 117 &&
      refBtnCheckInOut &&
      refBtnCheckInOut.current &&
      !refBtnCheckInOut.current.disabled
    ) {
      // key F6
      refBtnCheckInOut.current.click();
    }
  }

  return (
    <Container>
      <Formik
        enableReinitialize={true}
        validateOnMount={true}
        initialValues={state.model}
        validationSchema={MatchDaySchema}
        initialTouched={
          actions.isSelectedEmployee() && {
            "employee.person.name": true,
            "employee.person.identify": true,
            "employee.person.roleId": true,
            "employee.company": true,
            "employee.status": true,
            "employee.credentialNumber": true,
            "employee.checkInArea": true,
          }
        }
        onSubmit={(formValues, acts) => handleSaveEmployee("check", formValues)}
      >
        {({ values, errors, isValid, touched, handleSubmit, isSubmitting }) => (
          <>
            <Header>
              <HeaderData>
                <Autocomplete
                  name="event"
                  minLength={1}
                  keyField="id"
                  label="Evento"
                  value={_.get(values, "event")}
                  disabled={!canWrite}
                  valueFormat={(row) => `${row.name}`}
                  loadData={actions.eventsFinding}
                  emptyText={"Pesquise o evento"}
                  tipText={"Digite... "}
                  loadingText={"Carregando..."}
                  notFoundText={"Não encontrado"}
                  onChange={(value) => {
                    actions.selectEvent(value);
                    nextFocus(refEmployeeSearch);
                  }}
                />

                <Row span={4}>
                  <InputLabel
                    label="Tipo do evento"
                    value={_.get(values, "event.eventType.name")}
                  />
                  <InputLabel
                    label="Local do evento"
                    value={_.get(values, "event.place.name")}
                    noMargin
                  />
                  <InputLabel
                    label="Data/Hora início do evento"
                    value={formats.dateTimeZone(
                      _.get(values, "event.eventDate"),
                      "dd/MM/yyyy HH:mm"
                    )}
                    noMargin
                  />
                  <InputLabel
                    label="Data/Hora término do evento"
                    value={formats.dateTimeZone(
                      _.get(values, "event.eventEndDate"),
                      "dd/MM/yyyy HH:mm"
                    )}
                    noMargin
                  />
                </Row>
              </HeaderData>
              <img src={logo} alt="Palmeiras" />
            </Header>

            <Divider label="Acesso colaborador" />

            <FormData>
              <SearchContainer>
                <Row span={2}>
                  <Autocomplete
                    innerRef={refEmployeeSearch}
                    name="employee"
                    minLength={1}
                    keyField="id"
                    label="Nome, RG ou CPF..."
                    disabled={
                      !canWrite ||
                      !actions.isSelectedEvent() ||
                      actions.isAddMode()
                    }
                    valueFormat={(row) =>
                      `${row.name} (${_.get(row, "identify")})`
                    }
                    loadData={actions.employeesFinding}
                    emptyText={"Pesquise o colaborador"}
                    tipText={"Digite... "}
                    loadingText={"Carregando..."}
                    notFoundText={"Não encontrado"}
                    onChange={(value) => {
                      actions.selectEmployee(value);
                      nextFocus(refEmployeeInput);
                    }}
                  />

                  <InputItem>
                    <input
                      id="white-matchday-credential-search"
                      name="white-matchday-credential-search"
                      type="text"
                      disabled={
                        !canWrite ||
                        !actions.isSelectedEvent() ||
                        actions.isAddMode()
                      }
                      placeholder="Pesquisa por número da credencial"
                      onChange={handleChangeSearchCredentialNumber}
                      value={searchCredential}
                    />
                    <label htmlFor="white-matchday-credential-search">
                      Pesquisa por número da credencial
                    </label>
                    <MdFilterList
                      onClick={() =>
                        actions.searchByCredentialNumber(searchCredential)
                      }
                    />
                  </InputItem>
                </Row>
              </SearchContainer>

              <FormContainer>
                <Row span={2}>
                  <div className="left">
                    <InputGroup
                      innerRef={refEmployeeInput}
                      type="text"
                      name="employee.person.name"
                      label="Nome completo"
                      maxLength={100}
                      required={true}
                      disabled={!canWrite || !actions.isSelectedEmployee()}
                      hasError={
                        _.get(errors, "employee.person.name") &&
                        _.get(touched, "employee.person.name")
                      }
                      hideErrorLabel={true}
                    />

                    <Row span={2}>
                      <InputMask
                        disabled={!canWrite || !actions.isSelectedEmployee()}
                        mask={MASK_CPF}
                        required={true}
                        name="employee.person.identify"
                        label="CPF / Dcoumento identificação (estrangeiro)"
                        hasError={
                          _.get(errors, "employee.person.identify") &&
                          _.get(touched, "employee.person.identify")
                        }
                        hideErrorLabel={true}
                      />
                      <InputGroup
                        type="text"
                        disabled={!canWrite || !actions.isSelectedEmployee()}
                        name="employee.person.identityDoc"
                        label="RG"
                        hasError={
                          _.get(errors, "employee.person.identityDoc") &&
                          _.get(touched, "employee.person.identityDoc")
                        }
                        hideErrorLabel={true}
                      />
                    </Row>

                    {!actions.isAddMode() && (
                      <InputLabel
                        label="Empresa"
                        value={_.get(values, "employee.company.name")}
                      />
                    )}

                    {actions.isAddMode() && (
                      <Autocomplete
                        name="employee.company"
                        value={_.get(values, "employee.company")}
                        minLength={1}
                        keyField="id"
                        label="Empresa"
                        valueFormat={(row) => `${row.name}`}
                        loadData={actions.companiesFinding}
                        emptyText="Pesquise pela empresa..."
                        tipText={"Digite... "}
                        loadingText={"Carregando..."}
                        notFoundText={"Não encontrada"}
                        onChange={(value) =>
                          actions.selectCompany(values, value)
                        }
                        hasError={
                          _.get(errors, "employee.company") &&
                          _.get(touched, "employee.company")
                        }
                        hideErrorLabel={true}
                      />
                    )}

                    <Row span={2}>
                      <Select
                        disabled={!canWrite || !actions.isSelectedEmployee()}
                        name="employee.status"
                        label="Status *"
                        options={{
                          defaults: { value: "", label: "Informe o status" },
                          values: Object.keys(STATUS).map((key) => ({
                            value: key,
                            label: STATUS[key],
                          })),
                        }}
                        hasError={
                          _.get(errors, "employee.status") &&
                          _.get(touched, "employee.status")
                        }
                        hideErrorLabel={true}
                      />

                      <Select
                        disabled={!canWrite || !actions.isSelectedEmployee()}
                        name="employee.person.roleId"
                        label="Função *"
                        options={{
                          defaults: { value: "", label: "Qual função?" },
                          values: (state.roles || []).map((role) => ({
                            value: String(role.id),
                            label: role.name,
                          })),
                        }}
                        hasError={
                          _.get(errors, "employee.person.roleId") &&
                          _.get(touched, "employee.person.roleId")
                        }
                        hideErrorLabel={true}
                      />
                    </Row>

                    <Row span={3}>
                      <InputGroup
                        type="text"
                        disabled={!canWrite || !actions.isSelectedEmployee()}
                        label="Mídia"
                        name="employee.person.midia"
                      />
                      <InputGroup
                        type="text"
                        disabled={!canWrite || !actions.isSelectedEmployee()}
                        label="ACEESP"
                        name="employee.person.aceesp"
                      />
                      <InputGroup
                        type="text"
                        disabled={!canWrite || !actions.isSelectedEmployee()}
                        label="Tipo de acesso"
                        name="employee.tribuna"
                      />
                    </Row>
                  </div>

                  <div className="right">
                    <div className="access">
                      <div className="accessData">
                        <Select
                          disabled={!canWrite || !actions.isSelectedEmployee()}
                          name="employee.checkInArea"
                          label="Acesso *"
                          options={{
                            defaults: { value: "", label: "Área de acesso" },
                            values: _.get(values, "event.place.areas", []).map(
                              (row) => ({
                                value: row,
                                label: row,
                              })
                            ),
                          }}
                          hasError={
                            _.get(errors, "employee.checkInArea") &&
                            _.get(touched, "employee.checkInArea")
                          }
                          hideErrorLabel={true}
                        />

                        <InputGroup
                          type="text"
                          disabled={!canWrite || !actions.isSelectedEmployee()}
                          label="Observações"
                          name="employee.notes"
                        />

                        <InputGroup
                          id="white-matchday-pulseira"
                          type="text"
                          innerRef={refCredentialNumber}
                          disabled={!canWrite || !actions.isSelectedEmployee()}
                          label="Número da credencial / Código da pulseira *"
                          name="employee.credentialNumber"
                          hasError={
                            _.get(errors, "employee.credentialNumber") &&
                            _.get(touched, "employee.credentialNumber")
                          }
                          hideErrorLabel={true}
                        />
                      </div>

                      <div className="avatar">
                        <img
                          src={
                            _.get(values, "employee.person.photo") || userImage
                          }
                          alt=""
                        />
                      </div>
                    </div>

                    <Row span={2}>
                      <CheckArea>
                        <span>Check-in</span>
                        <strong>
                          {_.get(values, "employee.checkInDate") ||
                            "--/--/---- --:--"}
                        </strong>
                      </CheckArea>
                      <CheckArea>
                        <span>Check-out</span>
                        <strong>
                          {_.get(values, "employee.checkOutDate") ||
                            "--/--/---- --:--"}
                        </strong>
                      </CheckArea>
                    </Row>
                  </div>
                </Row>
              </FormContainer>

              <br />
              <Divider label="Substituição" />
              <br />

              <ReplaceContainer>
                <Row span={2}>
                  <Autocomplete
                    name="employee.substitute"
                    minLength={1}
                    keyField="id"
                    label="Colaborador substituído"
                    disabled={!canWrite || !actions.isSelectedEmployee()}
                    valueFormat={(row) => `${row.name}`}
                    loadData={actions.employeesFinding}
                    emptyText={"Pesquise o colaborador"}
                    tipText={"Digite... "}
                    loadingText={"Carregando..."}
                    notFoundText={"Não encontrado"}
                    value={_.get(values, "employee.substitute")}
                  />
                  <InputGroup
                    type="text"
                    disabled={!canWrite || !actions.isSelectedEmployee()}
                    label="Observações da substituição"
                    name="employee.notesReplace"
                  />
                </Row>
              </ReplaceContainer>
            </FormData>

            <Toolbar>
              <ImageButton
                title="Novo"
                width={115}
                height={70}
                disabled={
                  (!actions.isSelectedEvent() || actions.isSelectedEmployee() || !user || !user.isAdmin)
                }
                onClick={() => {
                  actions.newEmployee();
                  nextFocus(refEmployeeInput);
                }}
              >
                <MdAdd />
                <span>Novo</span>
              </ImageButton>

              <ImageButton
                title="Limpar"
                width={115}
                height={70}
                disabled={
                  !actions.isSelectedEvent() || !actions.isSelectedEmployee()
                }
                onClick={() => {
                  actions.selectEmployee({});
                  nextFocus(refEmployeeSearch);
                }}
              >
                <MdClear />
                <span>Limpar</span>
              </ImageButton>

              <ImageButton
                title="Protocolo COVID"
                width={115}
                height={70}
                disabled={
                  !actions.isSelectedEvent() ||
                  !actions.isSelectedEmployee() ||
                  !actions.hasPCovid()
                }
                onClick={actions.openCovidModal}
              >
                <MdViewModule />
                <span>P-COVID</span>
              </ImageButton>

              <ImageButton
                title="Salvar"
                width={115}
                height={70}
                disabled={
                  !actions.isSelectedEvent() || !isValid || isSubmitting
                }
                onClick={() => handleSaveEmployee("save", values)}
              >
                <MdSave />
                <span>Salvar</span>
              </ImageButton>
              <ImageButton
                ref={refBtnCheckInOut}
                type="submit"
                title={getButtonLabelCheck(values)}
                width={115}
                height={70}
                disabled={
                  !actions.isSelectedEvent() ||
                  !isValid ||
                  isSubmitting ||
                  isDisabledButtonCheck(values)
                }
                onClick={handleSubmit}
              >
                <MdCheckCircle />
                <span>{getButtonLabelCheck(values)}</span>
              </ImageButton>
            </Toolbar>

            <ProtocolCOVIDModal
              data={values}
              isOpen={state.showCovidModal}
              closeModal={actions.closeCovidModal}
              title={<>Protocolo COVID</>}
              width="60%"
            />
          </>
        )}
      </Formik>
    </Container>
  );
}

export default MatchDay;
